/**
 * `resolveRoomSprites` (R32) — which sprites the active room composites right
 * now, in stacking order.
 *
 * @remarks
 * A pure derived read, the `resolveRoomImage` precedent exactly: nothing is
 * written, nothing is stamped, and the transcript never learns a sprite
 * existed — the record is prose, so the export is unchanged. Each sprite's
 * `visibleWhen` evaluates through the SAME `evaluateCondition` the one
 * describer uses, at the same read-time boundary, so a composited kettle and
 * the prose about the kettle can never disagree about what the world is
 * doing. That equality is the D96 contract's whole point: the compositor is
 * a RENDERER of committed state, and this function is the projection it
 * renders.
 *
 * Order is the room's binding order (later entries stack above earlier
 * ones), filtered — never reordered — so stacking is authored, not derived.
 *
 * @packageDocumentation
 */

import type { RuntimeState, SpriteDefinition } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireRoom } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { EngineInvariantError } from '../execution/engine-invariant-error.js';

/**
 * Resolve the active room's visible sprites against the live world.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The sprites to composite, in stacking order (empty for none)
 */
export const resolveRoomSprites = (bundle: EngineGameBundle, state: RuntimeState): readonly SpriteDefinition[] => {
	const room = requireRoom(bundle, state.progression.activeRoomId);

	return room.sprites
		.map((spriteId) => {
			const sprite = bundle.indexes.spritesById.get(spriteId);

			// The loader's sprite gate resolves every binding before a bundle
			// exists — a miss here is a corrupt bundle or a fixture lying about
			// its sprites (the requireItem judgment, sprite-side).
			if (sprite === undefined) {
				throw new EngineInvariantError(
					`Sprite id "${spriteId}" has no definition in the bundle — the loader's sprite gate should have rejected this`,
				);
			}

			return sprite;
		})
		.filter((sprite) => evaluateCondition(sprite.visibleWhen, bundle, state));
};
